import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, from } from 'rxjs';
import { ApiConfig } from 'src/app/shared/api-config/api-config';
import { untilAborted } from 'src/app/shared/dashboard-grid';
import { LogHelper } from 'src/app/shared/logging/log-helper';
import { MetricDefinition, MetricsApi } from 'src/app/usage-api';

/** The Usage API's Kind for a Metric read as its current value, such as rooms in use */
const GAUGE_KIND = 'gauge';

/** The Usage API's Kind for a Metric read as a count over time, such as messages posted */
const COUNTER_KIND = 'counter';

/**
 * The tenant's Usage Metric Catalog, loaded once per page load, on first use. Each Kind has its own list, such as
 * `gauges()`; the catalog already holds only the Metrics the caller can read.
 */
@Injectable({ providedIn: 'root' })
export class MetricCatalog {
  private readonly metrics = inject(MetricsApi);
  private readonly catalog = signal<MetricDefinition[] | undefined>(undefined);
  /**
   * The request in flight or done, shared by every caller; cleared on a failure so the next caller asks again. It
   * starts on first use, so names and pickers fill in.
   */
  private request?: Promise<MetricDefinition[]> = this.fetch();

  /** The catalog is in hand, so a Metric it doesn't list is one the caller can't read, or that no longer exists */
  readonly loaded = computed(() => this.catalog() !== undefined);

  /** Every Gauge, deprecated ones included; empty while loading or when the catalog fails to load */
  private readonly allGauges = computed(() => this.ofKind(GAUGE_KIND));

  /** Every Counter, deprecated ones included; empty while loading or when the catalog fails to load */
  private readonly allCounters = computed(() => this.ofKind(COUNTER_KIND));

  /** The Gauges to offer for new tiles */
  readonly gauges = computed(() => this.allGauges().filter((gauge) => !gauge.deprecated));

  /** The Counters to offer for new tiles */
  readonly counters = computed(() => this.allCounters().filter((counter) => !counter.deprecated));

  /**
   * The catalog, for a widget's loader, so a catalog that is loading or failed is the tile's own loading or failure,
   * and its Retry asks again. Aborting stops this caller waiting, not the shared request.
   */
  load(abortSignal?: AbortSignal): Promise<MetricDefinition[]> {
    this.request ??= this.fetch();
    return abortSignal ? untilAborted(from(this.request), abortSignal) : this.request;
  }

  /** Finds deprecated Gauges too, so a tile saved before its Gauge was deprecated keeps the Gauge's name */
  findGauge(id: string): MetricDefinition | undefined {
    return this.allGauges().find((gauge) => gauge.id === id);
  }

  /** Finds deprecated Counters too, so a tile saved before its Counter was deprecated keeps the Counter's name */
  findCounter(id: string): MetricDefinition | undefined {
    return this.allCounters().find((counter) => counter.id === id);
  }

  private fetch(): Promise<MetricDefinition[]> {
    const request = firstValueFrom(this.metrics.metricsGetCatalog(ApiConfig.tenantId)).then(
      (catalog) => {
        this.catalog.set(catalog);
        return catalog;
      },
      (error: unknown) => {
        this.request = undefined;
        LogHelper.error('MetricCatalog', 'Failed to load the Usage Metric Catalog', error);
        throw error;
      },
    );
    // The failure is logged above; callers waiting on it get it too, but none need be waiting
    request.catch(() => undefined);
    return request;
  }

  private ofKind(kind: string): MetricDefinition[] {
    return (this.catalog() ?? []).filter((metric) => metric.kind === kind && !!metric.id);
  }
}
